import m from 'mithril';
import BaseComponent from '../../BaseComponent';
import FilterTagsDropdownView from './view/FilterTagsDropdownView';
import normalizeString from "../../../helpers/normalizeString";

export type TTagOption = {
    value: string;
    label: string;
    color: string | null;
    selected: boolean;
}

export default class FilterTagsDropdown extends BaseComponent {

    public static readonly SEARCH_THRESHOLD = 6;

    public options: TTagOption[] = [];
    public searchQuery = '';
    public highlightedIndex = -1;

    private $hiddenSelect: JQuery<HTMLSelectElement> | null = null;
    private view: FilterTagsDropdownView | null = null;

    public init(): void {
        const inputId = this.$component.data('filter-input-id');
        const hiddenSelect = document.getElementById(inputId) as HTMLSelectElement | null;
        if (!hiddenSelect) {
            console.warn(`FilterTagsDropdown: hidden select "${inputId}" not found`);
            return;
        }
        this.$hiddenSelect = $(hiddenSelect);

        this.options = this.parseOptions(hiddenSelect);

        this.view = new FilterTagsDropdownView(this);
        m.mount(this.$component[0], this.view);

        this.$component.parent().on('shown.bs.dropdown', () => {
            this.highlightedIndex = -1;
            m.redraw();
            this.focusSearch();
        });
    }

    public beforeRemove(): void {
        if (this.$component[0]) {
            m.mount(this.$component[0], null);
        }
    }

    public toggle(option: TTagOption): void {
        option.selected = !option.selected;
        this.syncHiddenSelect();
        requestAnimationFrame(() => this.focusSearch());
    }

    private focusSearch(): void {
        this.$component.find('.filter-select-dropdown-search input').trigger('focus');
    }

    public setSearchQuery(value: string): void {
        if (this.searchQuery !== value) {
            this.highlightedIndex = -1;
        }
        this.searchQuery = value;
    }

    public moveHighlight(delta: number): void {
        const options = this.getFilteredOptions();
        if (options.length === 0) {
            this.highlightedIndex = -1;
            return;
        }
        if (this.highlightedIndex === -1) {
            this.highlightedIndex = delta > 0 ? 0 : options.length - 1;
            return;
        }
        this.highlightedIndex = (this.highlightedIndex + delta + options.length) % options.length;
    }

    public showSearch(): boolean {
        return this.options.length >= FilterTagsDropdown.SEARCH_THRESHOLD;
    }

    public getFilteredOptions(): TTagOption[] {
        const q = normalizeString(this.searchQuery);
        if (q === '') {
            return this.options;
        }
        return this.options.filter(o => normalizeString(o.label).includes(q));
    }

    private parseOptions(select: HTMLSelectElement): TTagOption[] {
        const options: TTagOption[] = [];
        for (const optionEl of Array.from(select.options)) {
            if (optionEl.value === '') {
                continue;
            }
            options.push({
                value: optionEl.value,
                label: (optionEl.textContent ?? optionEl.value).trim(),
                color: optionEl.dataset.color || null,
                selected: optionEl.selected,
            });
        }
        return options;
    }

    private syncHiddenSelect(): void {
        if (!this.$hiddenSelect) {
            return;
        }

        const select = this.$hiddenSelect[0];
        const selectedValues = new Set(this.options.filter(o => o.selected).map(o => o.value));

        for (const optionEl of Array.from(select.options)) {
            optionEl.selected = selectedValues.has(optionEl.value);
        }

        this.$hiddenSelect.trigger('change');
    }
}
